import { useCallback, useEffect, useState } from 'react';
import { api, downloadFile, errorText, formatDateTime } from '../api';
import { useAuth } from '../auth';
import { Badge, Card, Empty, Loading, Notice, TextField } from '../components/ui';
import type { AuditEntry } from '../types';

interface Schedule {
  id: string;
  name: string;
  job_type: string;
  daily_at: string | null;
  interval_minutes: number | null;
  active: boolean;
  last_run_at: string | null;
  next_run_at: string;
  last_status: string | null;
}

interface FailedJob {
  id: string;
  type: string;
  attempts: number;
  last_error: string | null;
  updated_at: string;
}

type Zustand = 'gut' | 'faellig' | 'ueberfaellig' | 'nie';

interface Sicherungsstand {
  letzteSicherung: string | null;
  letzteProbe: string | null;
  letzteAuslagerung: string | null;
  stundenSeitSicherung: number | null;
  tageSeitProbe: number | null;
  stundenSeitAuslagerung: number | null;
  zustand: Zustand;
  probeZustand: Zustand;
  auslagerungZustand: Zustand;
  meldung: string;
  fehlerLetzte7Tage: number;
}

interface Sicherungsauftrag {
  id: string;
  art: 'sicherung' | 'rueckspielprobe' | 'einsicht';
  status: 'offen' | 'laeuft' | 'fertig' | 'fehler';
  anlass: string | null;
  quelle_datei: string | null;
  angefordert_am: string;
  beendet_am: string | null;
  meldung: string | null;
  ergebnis_datei: string | null;
  hat_ergebnis: boolean;
  angefordert_von_name: string | null;
}

interface WaehlbareSicherung {
  datei: string;
  zeitpunkt: string;
  groesse_bytes: number | null;
}

interface Sicherungsdaten {
  stand: Sicherungsstand;
  laeufe: Sicherungslauf[];
  auftraege: Sicherungsauftrag[];
  waehlbar: WaehlbareSicherung[];
}

interface Sicherungslauf {
  id: string;
  art: 'sicherung' | 'rueckspielprobe' | 'auslagerung';
  datei: string | null;
  groesse_bytes: number | null;
  dauer_ms: number | null;
  erfolg: boolean;
  meldung: string | null;
  geprueft: Record<string, number> | null;
  zeitpunkt: string;
}

interface SystemData {
  schedules: Schedule[];
  jobs: Record<string, number>;
  failed: FailedJob[];
}

const ZUSTAND: Record<Zustand, { kind: 'ok' | 'warn' | 'bad' | 'info'; text: string }> = {
  gut: { kind: 'ok', text: 'aktuell' },
  faellig: { kind: 'warn', text: 'fällig' },
  ueberfaellig: { kind: 'bad', text: 'überfällig' },
  nie: { kind: 'bad', text: 'nie gelaufen' },
};

function megabyte(bytes: number | null): string {
  if (bytes === null) return '—';
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

const STATUS_LABEL: Record<string, string> = {
  wartend: 'wartend',
  laeuft: 'läuft',
  fertig: 'fertig',
  fehler: 'Fehler',
};

const AUFTRAGSART: Record<Sicherungsauftrag['art'], string> = {
  sicherung: 'Sicherung',
  rueckspielprobe: 'Rückspielprobe',
  einsicht: 'Einsicht in eine Sicherung',
};

const AUFTRAGSSTAND: Record<
  Sicherungsauftrag['status'],
  { kind: 'ok' | 'warn' | 'bad' | 'info'; text: string }
> = {
  offen: { kind: 'info', text: 'wartet' },
  laeuft: { kind: 'warn', text: 'läuft' },
  fertig: { kind: 'ok', text: 'fertig' },
  fehler: { kind: 'bad', text: 'gescheitert' },
};

export function SystemPage() {
  const { can } = useAuth();
  const [data, setData] = useState<SystemData | null>(null);
  const [sicherung, setSicherung] = useState<Sicherungsdaten | null>(null);
  const [anlass, setAnlass] = useState('');
  const [quelle, setQuelle] = useState('');
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setError('');
    try {
      const [system, backups] = await Promise.all([
        api<SystemData>('/api/system/schedules'),
        api<Sicherungsdaten>('/api/system/backups'),
      ]);
      setData(system);
      setSicherung(backups);
    } catch (err) {
      setError(errorText(err));
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  /**
   * Einen Auftrag an die Datensicherung ablegen.
   *
   * Die Anwendung fuehrt ihn nicht selbst aus - sie legt ihn in eine Tabelle,
   * ein Cron-Auftrag auf dem Rechner arbeitet ihn ab. Deshalb steht danach
   * "wartet" da und nicht "fertig".
   */
  async function beauftrage(art: Sicherungsauftrag['art']) {
    setBusy(true);
    setError('');
    try {
      await api('/api/system/backups/auftraege', {
        method: 'POST',
        body: {
          art,
          ...(anlass.trim() ? { anlass: anlass.trim() } : {}),
          ...(art === 'einsicht' ? { quelleDatei: quelle } : {}),
        },
      });
      setAnlass('');
      setMessage(
        art === 'einsicht'
          ? 'Die Sicherung wird neben der laufenden Datenbank geöffnet. Das dauert einen Moment.'
          : 'Der Auftrag ist abgelegt und läuft innerhalb der nächsten Minute an.',
      );
      await load();
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  }

  async function toggle(schedule: Schedule) {
    setBusy(true);
    try {
      await api(`/api/system/schedules/${schedule.id}`, {
        method: 'PUT',
        body: { active: !schedule.active },
      });
      await load();
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  }

  async function runNow(schedule: Schedule) {
    setBusy(true);
    try {
      await api(`/api/system/schedules/${schedule.id}/run`, { method: 'POST' });
      setMessage(`"${schedule.name}" wurde eingereiht.`);
      setTimeout(() => void load(), 1500);
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  }

  async function retry(job: FailedJob) {
    setBusy(true);
    try {
      await api(`/api/system/jobs/${job.id}/retry`, { method: 'POST' });
      setMessage('Auftrag erneut eingereiht.');
      setTimeout(() => void load(), 1500);
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  }

  if (!data) return error ? <Notice kind="error">{error}</Notice> : <Loading />;

  return (
    <>
      {error && <Notice kind="error">{error}</Notice>}
      {message && <Notice kind="success">{message}</Notice>}

      {sicherung && sicherung.stand.zustand !== 'gut' && (
        <Notice kind="error">
          <strong>Datensicherung: {sicherung.stand.meldung}</strong> Solange das so bleibt, sind alle
          Buchungen seit dem letzten Lauf nur einmal vorhanden.
        </Notice>
      )}

      {sicherung && (
        <Card title="Datensicherung" padded={false}>
          <div className="body">
            <div className="small muted" style={{ marginBottom: 12 }}>
              Gesichert wird nächtlich ausserhalb der Anwendung: Datenbank und Rollen, verschlüsselt
              abgelegt, taeglich zwei Wochen lang, sonntags zwei Monate, zum Monatsersten ein Jahr.
              Hier steht nur, ob es auch passiert ist — ob sich die Sicherung zurückspielen
              lässt, und ob eine Kopie an einen zweiten Ort gegangen ist. Eine Sicherung, die noch
              nie zurückgespielt wurde, ist eine Vermutung; eine, die nur auf diesem Server liegt,
              hilft gegen einen Serververlust nicht.
            </div>
            <div className="grid kpi">
              <div
                className={sicherung.stand.zustand === 'gut' ? 'kpi-tile' : 'kpi-tile alert'}
              >
                <div className="label">Letzte Sicherung</div>
                <div className="value" style={{ fontSize: 18 }}>
                  {sicherung.stand.letzteSicherung
                    ? formatDateTime(sicherung.stand.letzteSicherung)
                    : 'noch keine'}
                </div>
                <div className="hint">
                  <Badge kind={ZUSTAND[sicherung.stand.zustand].kind}>
                    {ZUSTAND[sicherung.stand.zustand].text}
                  </Badge>
                </div>
              </div>
              <div
                className={sicherung.stand.probeZustand === 'gut' ? 'kpi-tile' : 'kpi-tile alert'}
              >
                <div className="label">Letzte Rückspielprobe</div>
                <div className="value" style={{ fontSize: 18 }}>
                  {sicherung.stand.letzteProbe
                    ? formatDateTime(sicherung.stand.letzteProbe)
                    : 'noch keine'}
                </div>
                <div className="hint">
                  <Badge kind={ZUSTAND[sicherung.stand.probeZustand].kind}>
                    {ZUSTAND[sicherung.stand.probeZustand].text}
                  </Badge>
                </div>
              </div>
              <div
                className={
                  sicherung.stand.auslagerungZustand === 'nie'
                    ? 'kpi-tile'
                    : sicherung.stand.auslagerungZustand === 'gut'
                      ? 'kpi-tile'
                      : 'kpi-tile alert'
                }
              >
                <div className="label">Letzte Auslagerung</div>
                <div className="value" style={{ fontSize: 18 }}>
                  {sicherung.stand.letzteAuslagerung
                    ? formatDateTime(sicherung.stand.letzteAuslagerung)
                    : 'kein Ziel'}
                </div>
                <div className="hint">
                  {sicherung.stand.auslagerungZustand === 'nie' ? (
                    <span className="muted">kein zweiter Ort konfiguriert</span>
                  ) : (
                    <Badge kind={ZUSTAND[sicherung.stand.auslagerungZustand].kind}>
                      {ZUSTAND[sicherung.stand.auslagerungZustand].text}
                    </Badge>
                  )}
                </div>
              </div>
              <div
                className={sicherung.stand.fehlerLetzte7Tage > 0 ? 'kpi-tile alert' : 'kpi-tile'}
              >
                <div className="label">Fehlversuche</div>
                <div className="value">{sicherung.stand.fehlerLetzte7Tage}</div>
                <div className="hint">in den letzten sieben Tagen</div>
              </div>
            </div>
          </div>
          {sicherung.laeufe.length === 0 ? (
            <Empty>Es wurde noch kein Lauf protokolliert.</Empty>
          ) : (
            <table>
              <thead>
                <tr>
                  <th>Zeitpunkt</th>
                  <th>Art</th>
                  <th>Datei</th>
                  <th className="num">Größe</th>
                  <th className="num">Dauer</th>
                  <th>Ergebnis</th>
                </tr>
              </thead>
              <tbody>
                {sicherung.laeufe.map((lauf) => (
                  <tr key={lauf.id}>
                    <td className="small muted">{formatDateTime(lauf.zeitpunkt)}</td>
                    <td className="small">
                      {lauf.art === 'sicherung'
                        ? 'Sicherung'
                        : lauf.art === 'auslagerung'
                          ? 'Auslagerung'
                          : 'Rückspielprobe'}
                    </td>
                    <td className="small mono">{lauf.datei ?? '—'}</td>
                    <td className="num small">{megabyte(lauf.groesse_bytes)}</td>
                    <td className="num small">
                      {lauf.dauer_ms === null ? '—' : `${(lauf.dauer_ms / 1000).toFixed(1)} s`}
                    </td>
                    <td>
                      <Badge kind={lauf.erfolg ? 'ok' : 'bad'}>
                        {lauf.erfolg ? 'in Ordnung' : 'gescheitert'}
                      </Badge>
                      {lauf.meldung && (
                        <div className="small muted" style={{ marginTop: 4 }}>
                          {lauf.meldung}
                        </div>
                      )}
                      {lauf.geprueft && (
                        <div className="small muted" style={{ marginTop: 4 }}>
                          {Object.entries(lauf.geprueft)
                            .map(([k, v]) => `${k}: ${v}`)
                            .join(' · ')}
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Card>
      )}

      {sicherung && can('admin') && (
        <Card title="Sicherung von Hand">
          <div className="small muted" style={{ marginBottom: 14 }}>
            Vor einem grossen Import, vor dem Jahresabschluss, vor jedem Schritt, bei dem man
            hinterher gern zurückschauen möchte. Die Anwendung führt das nicht selbst aus — sie legt
            einen Auftrag ab, den der Rechner innerhalb einer Minute abarbeitet.
          </div>

          <div className="form-grid">
            <TextField
              label="Anlass (optional)"
              value={anlass}
              onChange={setAnlass}
              placeholder="vor Import Lieferantenliste"
              hint="beantwortet in einem halben Jahr die Frage, warum hier gesichert wurde"
            />
          </div>

          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginTop: 4 }}>
            <button className="primary" onClick={() => beauftrage('sicherung')} disabled={busy}>
              Sicherung jetzt erstellen
            </button>
            <button onClick={() => beauftrage('rueckspielprobe')} disabled={busy}>
              Rückspielprobe starten
            </button>
          </div>

          <div style={{ borderTop: '1px solid var(--line)', margin: '20px 0 16px' }} />

          <h4 style={{ margin: '0 0 8px' }}>Einen früheren Stand einsehen</h4>
          <div className="small muted" style={{ marginBottom: 12 }}>
            Die gewählte Sicherung wird <strong>neben</strong> der laufenden Datenbank geöffnet und
            als GoBD-Datenträger herausgegeben: alle Tabellen als CSV, lesbar in Excel. Danach
            verschwindet die Kopie wieder. Der laufende Bestand wird dabei nicht angefasst — wer
            sieht, was damals stand, korrigiert im laufenden System vorwärts.
          </div>

          {sicherung.waehlbar.length === 0 ? (
            <Empty>Es liegt noch keine Sicherung vor.</Empty>
          ) : (
            <>
              <div className="form-grid">
                <div className="field">
                  <label>Sicherung</label>
                  <select value={quelle} onChange={(e) => setQuelle(e.target.value)}>
                    <option value="">— bitte wählen —</option>
                    {sicherung.waehlbar.map((eintrag) => (
                      <option key={eintrag.datei} value={eintrag.datei}>
                        {formatDateTime(eintrag.zeitpunkt)} · {megabyte(eintrag.groesse_bytes)}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              <button onClick={() => beauftrage('einsicht')} disabled={busy || !quelle}>
                Stand einsehen
              </button>
            </>
          )}

          <Notice kind="info">
            Ein Zurückspielen <strong>über</strong> die laufende Datenbank gibt es hier bewusst
            nicht. Es würde jede Buchung seit der Sicherung verwerfen und wäre ein Schreibweg am
            Bewegungsjournal vorbei — genau das, was die Unveränderbarkeit ausschliesst. Für den
            Ernstfall, in dem die Datenbank wirklich verloren ist, steht der Weg in
            <span className="mono"> ops/DATENSICHERUNG.md</span>; dann läuft ohnehin auch diese
            Oberfläche nicht mehr.
          </Notice>

          {sicherung.auftraege.length > 0 && (
            <table style={{ marginTop: 16 }}>
              <thead>
                <tr>
                  <th>Angefordert</th>
                  <th>Art</th>
                  <th>Anlass</th>
                  <th>Durch</th>
                  <th>Stand</th>
                  <th>Ergebnis</th>
                </tr>
              </thead>
              <tbody>
                {sicherung.auftraege.map((auftrag) => (
                  <tr key={auftrag.id}>
                    <td className="small muted">{formatDateTime(auftrag.angefordert_am)}</td>
                    <td className="small">{AUFTRAGSART[auftrag.art]}</td>
                    <td className="small">
                      {auftrag.anlass ?? '—'}
                      {auftrag.quelle_datei && (
                        <div className="small mono muted">{auftrag.quelle_datei}</div>
                      )}
                    </td>
                    <td className="small">{auftrag.angefordert_von_name ?? '—'}</td>
                    <td>
                      <Badge kind={AUFTRAGSSTAND[auftrag.status].kind}>
                        {AUFTRAGSSTAND[auftrag.status].text}
                      </Badge>
                      {auftrag.meldung && (
                        <div className="small muted" style={{ marginTop: 4 }}>
                          {auftrag.meldung}
                        </div>
                      )}
                    </td>
                    <td className="small">
                      {auftrag.hat_ergebnis ? (
                        <button
                          className="small"
                          onClick={() =>
                            downloadFile(`/api/system/backups/auftraege/${auftrag.id}/datei`)
                          }
                        >
                          Datenträger laden
                        </button>
                      ) : auftrag.ergebnis_datei ? (
                        <span className="muted">nach 7 Tagen entfernt</span>
                      ) : (
                        '—'
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Card>
      )}

      <Card title="Zeitpläne" padded={false}>
        <div className="body small muted" style={{ paddingBottom: 0 }}>
          Wiederkehrende Aufträge laufen über die Job-Queue. Der Bestandsschnappschuss ist die
          Grundlage der Lagergeldabrechnung — fällt ein Tag aus, fehlt er in der Rechnung.
        </div>
        {data.schedules.length === 0 ? (
          <Empty>Keine Zeitpläne hinterlegt.</Empty>
        ) : (
          <table>
            <thead>
              <tr>
                <th>Aufgabe</th>
                <th>Rhythmus</th>
                <th>Zuletzt</th>
                <th>Nächster Lauf</th>
                <th>Status</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {data.schedules.map((schedule) => (
                <tr key={schedule.id}>
                  <td>
                    {schedule.name}
                    <div className="small muted mono">{schedule.job_type}</div>
                  </td>
                  <td className="small">
                    {schedule.daily_at
                      ? `täglich ${schedule.daily_at} Uhr`
                      : `alle ${schedule.interval_minutes} Minuten`}
                  </td>
                  <td className="small muted">
                    {schedule.last_run_at ? formatDateTime(schedule.last_run_at) : 'nie'}
                  </td>
                  <td className="small">{formatDateTime(schedule.next_run_at)}</td>
                  <td>
                    {schedule.active ? (
                      <Badge kind="ok">aktiv</Badge>
                    ) : (
                      <Badge kind="bad">pausiert</Badge>
                    )}
                  </td>
                  <td className="num">
                    <button className="small ghost" onClick={() => runNow(schedule)} disabled={busy}>
                      jetzt starten
                    </button>
                    <button className="small ghost" onClick={() => toggle(schedule)} disabled={busy}>
                      {schedule.active ? 'pausieren' : 'aktivieren'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      <div className="grid two">
        <Card title="Hintergrundaufträge">
          {Object.keys(data.jobs).length === 0 ? (
            <Empty>Noch keine Aufträge gelaufen.</Empty>
          ) : (
            <div className="grid kpi">
              {Object.entries(data.jobs).map(([status, count]) => (
                <div
                  key={status}
                  className={status === 'fehler' && count > 0 ? 'kpi-tile alert' : 'kpi-tile'}
                >
                  <div className="label">{STATUS_LABEL[status] ?? status}</div>
                  <div className="value">{count}</div>
                </div>
              ))}
            </div>
          )}
        </Card>

        <Card title="Fehlgeschlagene Aufträge" padded={false}>
          {data.failed.length === 0 ? (
            <Empty>Nichts fehlgeschlagen.</Empty>
          ) : (
            <table>
              <thead>
                <tr>
                  <th>Typ</th>
                  <th className="num">Versuche</th>
                  <th>Fehler</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {data.failed.map((job) => (
                  <tr key={job.id}>
                    <td className="mono small">{job.type}</td>
                    <td className="num">{job.attempts}</td>
                    <td className="small" style={{ color: 'var(--bad)' }}>
                      {job.last_error?.slice(0, 80) ?? '—'}
                    </td>
                    <td className="num">
                      <button className="small ghost" onClick={() => retry(job)} disabled={busy}>
                        wiederholen
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Card>
      </div>

      <ChangeLog />
      <Anmeldeprotokoll />
    </>
  );
}

interface Anmeldeversuch {
  id: string;
  email: string;
  erfolg: boolean;
  grund: string | null;
  ip: string | null;
  user_agent: string | null;
  zeitpunkt: string;
  benutzer: string | null;
}

interface Auffaellig {
  email: string;
  versuche: number;
  letzter: string;
  ips: number;
}

/**
 * Wer sich wann angemeldet hat - und wer es vergeblich versucht hat.
 *
 * Die Fehlversuche stehen oben und nicht unten: eine Reihe davon auf dieselbe
 * Adresse ist das einzige frühe Zeichen, an dem sich ein Angriff erkennen
 * lässt.
 */
function Anmeldeprotokoll() {
  const [versuche, setVersuche] = useState<Anmeldeversuch[]>([]);
  const [auffaellig, setAuffaellig] = useState<Auffaellig[]>([]);
  const [nurFehl, setNurFehl] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    let aktuell = true;
    api<{ versuche: Anmeldeversuch[]; auffaellig: Auffaellig[] }>('/api/system/logins', {
      query: { nurFehlversuche: nurFehl ? 'true' : undefined, limit: '60' },
    })
      .then((daten) => {
        if (!aktuell) return;
        setVersuche(daten.versuche);
        setAuffaellig(daten.auffaellig);
      })
      .catch((err) => aktuell && setError(errorText(err)));
    return () => {
      aktuell = false;
    };
  }, [nurFehl]);

  return (
    <Card
      title="Anmeldungen"
      actions={
        <button className="small" onClick={() => setNurFehl(!nurFehl)}>
          {nurFehl ? 'alle zeigen' : 'nur Fehlversuche'}
        </button>
      }
      padded={false}
    >
      {error && (
        <div className="body">
          <Notice kind="error">{error}</Notice>
        </div>
      )}

      {auffaellig.length > 0 && (
        <div className="body" style={{ paddingBottom: 0 }}>
          <Notice kind="info">
            <strong>Auffällig in den letzten 24 Stunden:</strong>{' '}
            {auffaellig
              .map(
                (eintrag) =>
                  `${eintrag.email} (${eintrag.versuche} Fehlversuche` +
                  `${eintrag.ips > 1 ? `, ${eintrag.ips} Adressen` : ''})`,
              )
              .join(' · ')}
          </Notice>
        </div>
      )}

      {versuche.length === 0 ? (
        <Empty>Nichts protokolliert.</Empty>
      ) : (
        <table>
          <thead>
            <tr>
              <th>Zeitpunkt</th>
              <th>Adresse</th>
              <th>Benutzer</th>
              <th>Ergebnis</th>
              <th>Herkunft</th>
            </tr>
          </thead>
          <tbody>
            {versuche.map((versuch) => (
              <tr key={versuch.id}>
                <td className="small">{formatDateTime(versuch.zeitpunkt)}</td>
                <td className="small mono">{versuch.email}</td>
                <td className="small">{versuch.benutzer ?? <span className="muted">—</span>}</td>
                <td>
                  {versuch.erfolg ? (
                    <Badge kind="ok">angemeldet</Badge>
                  ) : (
                    <Badge kind="bad">{versuch.grund ?? 'abgelehnt'}</Badge>
                  )}
                </td>
                <td className="small muted mono">{versuch.ip ?? '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </Card>
  );
}

/** Sprechende Namen für die protokollierten Felder. */
const FIELD_LABEL: Record<string, string> = {
  name: 'Name',
  sku: 'Artikelnummer',
  is_customer: 'Kunde',
  is_supplier: 'Lieferant',
  is_owner: 'Warenbesitzer',
  active: 'aktiv',
  email: 'E-Mail',
  phone: 'Telefon',
  street: 'Straße',
  zip: 'PLZ',
  city: 'Ort',
  country: 'Land',
  storage_millicents_per_unit_day: 'Lagersatz',
  handling_in_cents: 'Handling Eingang',
  handling_out_cents: 'Handling Ausgang',
  min_monthly_cents: 'Mindestumsatz',
  tax_rate_percent: 'Steuersatz',
  small_business: 'Kleinunternehmer',
  payment_terms_days: 'Zahlungsziel (Tage)',
};

/** Leere Werte sollen als Strich erscheinen, Wahrheitswerte als Ja/Nein. */
function valueText(value: unknown): string {
  if (value === null || value === undefined || value === '') return '—';
  if (typeof value === 'boolean') return value ? 'ja' : 'nein';
  return String(value);
}

/**
 * Wer hat wann welche Stammdaten geändert. Das Bewegungsjournal ist ohnehin
 * unveränderlich - hier geht es um die Daten darüber: Tarife, Anschriften,
 * Steuermerkmale.
 */
function ChangeLog() {
  const [entries, setEntries] = useState<AuditEntry[]>([]);
  const [entities, setEntities] = useState<string[]>([]);
  const [entity, setEntity] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    let aktuell = true;
    api<{ entries: AuditEntry[]; entities: string[] }>('/api/system/audit', {
      query: { entity: entity || undefined, limit: '100' },
    })
      .then((data) => {
        if (!aktuell) return;
        setEntries(data.entries);
        setEntities(data.entities);
      })
      .catch((err) => aktuell && setError(errorText(err)));
    return () => {
      aktuell = false;
    };
  }, [entity]);

  return (
    <Card
      title="Änderungsprotokoll"
      actions={
        <select value={entity} onChange={(e) => setEntity(e.target.value)}>
          <option value="">alle Bereiche</option>
          {entities.map((name) => (
            <option key={name} value={name}>
              {name}
            </option>
          ))}
        </select>
      }
      padded={false}
    >
      {error && (
        <div className="body">
          <Notice kind="error">{error}</Notice>
        </div>
      )}
      {entries.length === 0 ? (
        <Empty>Noch nichts protokolliert.</Empty>
      ) : (
        <table>
          <thead>
            <tr>
              <th>Zeitpunkt</th>
              <th>Benutzer</th>
              <th>Bereich</th>
              <th>Datensatz</th>
              <th>Vorgang</th>
              <th>Änderungen</th>
            </tr>
          </thead>
          <tbody>
            {entries.map((entry) => (
              <tr key={entry.id}>
                <td className="small">{formatDateTime(entry.created_at)}</td>
                <td className="small">{entry.user_name ?? 'System'}</td>
                <td className="small">{entry.entity}</td>
                <td className="small">{entry.label ?? '—'}</td>
                <td>
                  <Badge kind={entry.action === 'geloescht' ? 'bad' : entry.action === 'angelegt' ? 'ok' : 'info'}>
                    {entry.action === 'geaendert' ? 'geändert' : entry.action === 'geloescht' ? 'gelöscht' : 'angelegt'}
                  </Badge>
                </td>
                <td className="small">
                  {entry.changes.length === 0
                    ? '—'
                    : entry.changes.map((change) => (
                        <div key={change.field}>
                          {FIELD_LABEL[change.field] ?? change.field}: {valueText(change.from)} →{' '}
                          {valueText(change.to)}
                        </div>
                      ))}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </Card>
  );
}
